import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { uploadAttachments } from "../../lib/attachments";
import { ImageStrip } from "../../components/vc/ImageStrip";
import { Header } from "../../components/vc/Header";
import { UploadCard } from "../../components/vc/UploadCard";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { Section } from "../../components/vc/layout";
import {
  ArrowLeft, FileSpreadsheet, Image as ImageIcon, Pencil, Plus, Trash2, FileText, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Sheet, SheetContent } from "../../components/ui/sheet";

const UNITS = ["pcs", "kg", "ltr", "box", "bag", "meter", "gram", "dozen"];

export default function VCRaiseEnquiry() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("select"); // select | manual | excel-parsed | image
  const [manualRows, setManualRows] = useState([{ product: "", quantity: "", unit: "pcs" }]);
  const [excelBusy, setExcelBusy] = useState(false);
  const [imagePreview, setImagePreview] = useState("");  // legacy single-image fallback for older callers
  const [attachments, setAttachments] = useState([]);   // up to 5 image data URLs (also from converted PDFs)
  const [uploading, setUploading] = useState(false);
  const [excelHelpOpen, setExcelHelpOpen] = useState(false);

  const addRow = () =>
    setManualRows((r) => [...r, { product: "", quantity: "", unit: "pcs" }]);

  const updateRow = (i, k, v) =>
    setManualRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  const removeRow = (i) =>
    setManualRows((r) => r.filter((_, idx) => idx !== i));

  const goReview = (items, meta = {}) => {
    navigate("/enquiries/review", {
      state: { items, ...meta },
    });
  };

  const submitManual = () => {
    const items = manualRows
      .filter((r) => r.product.trim() && Number(r.quantity) > 0)
      .map((r) => ({ product: r.product.trim(), quantity: Number(r.quantity), unit: r.unit || "pcs" }));
    if (items.length === 0) { toast.error("Add at least one valid item"); return; }
    goReview(items);
  };

  const onExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExcelBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post("/import/parse", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const items = (data.items || []).map((it) => ({
        product: it.product, quantity: it.quantity, unit: it.unit || "pcs",
      }));
      if (items.length === 0) { toast.error("No items found in file"); return; }
      toast.success(`${items.length} items extracted`);
      goReview(items, { source: "excel" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Excel parse failed");
    } finally {
      setExcelBusy(false);
      e.target.value = "";
    }
  };

  const onImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const { images } = await uploadAttachments(files, { existingCount: attachments.length });
      if (images.length === 0) {
        return; // toast already shown by helper
      }
      const merged = [...attachments, ...images].slice(0, 5);
      setAttachments(merged);
      setImagePreview(merged[0] || "");
      setMode("image");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = ""; // allow re-selecting the same file
    }
  };

  const submitImage = () => {
    // Image-only enquiry: pass all attachments — Review page lets user add items manually
    goReview([], {
      source: "image",
      image_data: attachments[0] || imagePreview || "",
      images: attachments,
    });
  };

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-40 space-y-5">
        <button onClick={() => navigate(-1)} data-testid="raise-enquiry-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Raise Enquiry</h2>
          <p className="text-sm text-slate-500 mt-0.5">Choose how you want to add items</p>
        </div>

        {/* Method tiles — always visible */}
        <div className="space-y-2.5">
          <UploadCard
            title="Upload Excel"
            subtitle="XLSX or CSV · quick bulk import"
            Icon={FileSpreadsheet}
            tone="emerald"
            accept=".xlsx,.csv"
            onChange={onExcel}
            testid="method-excel"
          />
          <a
            onClick={(e) => { e.preventDefault(); setExcelHelpOpen(true); }}
            href="#excel-preview-format"
            data-testid="excel-preview-format-btn"
            className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 ml-2 hover:underline cursor-pointer"
          >
            <FileText className="w-3 h-3" /> Excel Preview Format
          </a>

          <UploadCard
            title={uploading ? "Uploading..." : "Upload Image / PDF"}
            subtitle="Up to 5 images or PDFs · we'll convert PDF pages"
            Icon={ImageIcon}
            tone="indigo"
            accept="image/*,application/pdf,.pdf"
            multiple
            onChange={onImage}
            testid="method-image"
          />

          <UploadCard
            title="Manual Entry"
            subtitle="Type products one by one"
            Icon={Pencil}
            tone="amber"
            onClick={() => setMode(mode === "manual" ? "select" : "manual")}
            testid="method-manual"
          />
        </div>

        {/* Excel busy indicator */}
        {excelBusy && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-800 font-semibold">
            Parsing your Excel...
          </div>
        )}

        {/* Manual mode */}
        {mode === "manual" && (
          <Section title="Items" subtitle="Add products for your enquiry" testid="manual-section">
            <div className="space-y-2">
              {manualRows.map((row, i) => (
                <div
                  key={i}
                  data-testid={`manual-row-${i}`}
                  className="bg-white border border-slate-100 rounded-2xl p-2 shadow-sm"
                >
                  {/* Compact single row: Product · Qty · UOM · Delete */}
                  <div className="flex items-center gap-1.5">
                    <Input
                      data-testid={`manual-product-${i}`}
                      value={row.product}
                      onChange={(e) => updateRow(i, "product", e.target.value)}
                      placeholder="Product name"
                      className="h-10 rounded-lg flex-1 min-w-0 text-sm"
                    />
                    <Input
                      data-testid={`manual-qty-${i}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.quantity}
                      onChange={(e) => updateRow(i, "quantity", e.target.value)}
                      placeholder="Qty"
                      className="h-10 rounded-lg w-14 text-sm text-right tabular-nums"
                    />
                    <Select value={row.unit} onValueChange={(v) => updateRow(i, "unit", v)}>
                      <SelectTrigger data-testid={`manual-unit-${i}`} className="h-10 rounded-lg w-[68px] text-sm px-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => removeRow(i)}
                      data-testid={`manual-delete-${i}`}
                      className="w-9 h-10 shrink-0 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 grid place-items-center transition"
                      aria-label="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              <SecondaryButton onClick={addRow} data-testid="manual-add-row" className="w-full">
                <Plus className="w-4 h-4 inline mr-1" /> Add Another Item
              </SecondaryButton>
            </div>
          </Section>
        )}

        {/* Image / PDF mode — strip of thumbnails (up to 5) */}
        {mode === "image" && attachments.length > 0 && (
          <Section title="Uploaded images" subtitle={`${attachments.length}/5 pages`} testid="image-section">
            <div className="bg-white rounded-2xl border border-slate-100 p-3 space-y-3">
              <ImageStrip
                images={attachments}
                onRemove={(i) => {
                  const next = attachments.filter((_, idx) => idx !== i);
                  setAttachments(next);
                  setImagePreview(next[0] || "");
                  if (next.length === 0) setMode("select");
                }}
                testid="raise-image-strip"
              />
              <div className="grid grid-cols-2 gap-2">
                <label
                  htmlFor="reupload"
                  className={`h-11 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-700 font-semibold text-sm grid place-items-center cursor-pointer transition ${attachments.length >= 5 ? "opacity-40 pointer-events-none" : ""}`}
                >
                  {attachments.length >= 5 ? "Limit reached" : "Add more"}
                </label>
                <input id="reupload" type="file" accept="image/*,application/pdf,.pdf" multiple onChange={onImage} className="hidden" data-testid="raise-image-add-more" />
                <button
                  onClick={() => { setAttachments([]); setImagePreview(""); setMode("select"); }}
                  data-testid="image-remove"
                  className="h-11 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-sm transition"
                >
                  Remove all
                </button>
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-xl p-3">
                Suppliers will see every page as an image. PDFs are converted automatically. Maximum 5 pages per enquiry.
              </div>
            </div>
          </Section>
        )}

        {/* Next button */}
        {(mode === "manual" || mode === "image") && (
          <PrimaryButton
            onClick={mode === "manual" ? submitManual : submitImage}
            data-testid="raise-enquiry-next"
            className="w-full flex items-center justify-center gap-2"
          >
            Next <ArrowRight className="w-4 h-4" />
          </PrimaryButton>
        )}
      </div>

      {/* #6 — Excel Preview Format modal (half-page bottom sheet on all screens) */}
      <Sheet open={excelHelpOpen} onOpenChange={setExcelHelpOpen}>
        <SheetContent
          side="bottom"
          data-testid="excel-preview-modal"
          className="p-0 rounded-t-3xl h-[55vh] max-h-[55vh] flex flex-col gap-0"
        >
          <div className="px-4 pt-4 pb-2 shrink-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Format</div>
            <div className="text-base font-bold text-slate-900">Excel Preview Format</div>
            <div className="text-xs text-slate-500 mt-1 pr-8">
              Keep the Product Name in a separate cell. Quantity and Unit can either be entered separately or together in the next cell.
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3 min-h-0">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">✓ Accepted</div>
              <div className="font-mono text-xs bg-emerald-50 border border-emerald-100 rounded-xl p-2">
                Basmati rice 25kg | 50 | bags
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">✓ Accepted</div>
              <div className="font-mono text-xs bg-emerald-50 border border-emerald-100 rounded-xl p-2">
                Basmati rice 25kg | 50 bags
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-red-700 mb-1">✕ Not accepted</div>
              <div className="font-mono text-xs bg-red-50 border border-red-100 rounded-xl p-2">
                Basmati rice 25kg 50 bags
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
